/* The hover peek's box, transcribed from the 07.09 mock's `peekSt` and the objects under it.
 *
 * ── Where it is centred, and why that is the mock rather than the brief ──────────────────────
 * The mock's `top: 50%` is taken against the LIBRARY, not against the hovered row: `peekSt` is a
 * child of the 320px drawer, whose own box is the panel's full height, so the card sits at the
 * middle of the drawer and stays there as the pointer runs down the list. Reproduced as measured.
 * Centring on the row instead would put the card off the top of the panel for the first rows and
 * off the bottom for the last, on a list of 159 — the mock could afford a row-anchored card only
 * because its pool is 18 rows deep.
 *
 * ── It cannot widen or scroll anything ───────────────────────────────────────────────────────
 * `position: absolute` inside the rail, so it is out of flow in a box that does not scroll. The
 * scroller is the results `<ul>` below it, and the card is not inside it. `pointer-events: none`
 * means it can neither take a press aimed at a row nor hold a hover of its own. */
.peek {
  position: absolute;
  top: 50%;
  left: calc(100% + 10px);
  z-index: 31;
  display: flex;
  flex-direction: column;
  width: 236px;
  overflow: hidden;
  pointer-events: none;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-16px);
  box-shadow: var(--shadow-pop);
  transform: translateY(-50%);

  /* The mock's `.24s cubic-bezier(.16,.84,.28,1)`, snapped to the app's own pair: `--dur-base` is
   * 200ms and `--ease-fp` is `cubic-bezier(.2,.8,.3,1)`, which is the same curve to within a
   * hundredth on each handle. Minting a duration and an easing token for one declaration would put
   * two names in the token layer that nobody can read against anything else. */
  animation: peek-in var(--dur-base) var(--ease-fp) both;
}

/* The mock's `tipIn`: a 6px slide from the drawer's edge with the vertical centring held THROUGH the
 * keyframes, because `transform` is one property and a keyframe that names only the slide would drop
 * the `translateY(-50%)` for the length of the animation and the card would jump down half its own
 * height on entry. */
@keyframes peek-in {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(-6px) scale(0.97);
  }

  to {
    opacity: 1;
    transform: translateY(-50%);
  }
}

/* The reduced-motion answer is the END of the animation held from the first frame, never a delay.
 * Collapsing the duration alone leaves the `both` fill showing the FROM frame until it elapses. */
@media (prefers-reduced-motion: reduce) {
  .peek {
    animation: none;
  }
}

.media {
  display: block;
  flex: none;
  width: 100%;
  height: 189px;
  object-position: center 40%;
  background: var(--bg-sunken);
}

.mediaFallback {
  display: grid;
  place-items: center;
}

.mediaGlyph {
  width: 54px;
  height: 54px;
}

.body {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
  padding: var(--space-11px) var(--space-3) var(--space-13px);
}

.head {
  display: flex;
  flex-direction: column;
  gap: var(--space-7px);
}

.name {
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-plan);
  line-height: var(--lh-tight);
  color: var(--text);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  align-items: center;
}

/* The category chip — the mock's `peekChips`, and the same pattern the filter panel's rows draw. */
.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text);
  white-space: nowrap;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-pill);
}


.engagement {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  align-items: center;
}

/* Front and back, side by side. `--h-silhouette` is the contract `BodySilhouette` reads for its own
 * box — the one seam that module opens — so the pair is resized through it rather than by
 * redeclaring the figure's geometry here. 132px because the card already spends 189 on the still
 * and the whole of it has to stand inside a drawer at the 620px viewport floor. */
.figures {
  --h-silhouette: 132px;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
  width: 100%;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5px);
  align-items: center;
  justify-content: center;
}

/* ── The pills speak the PROGRAMS' language (owner, 17 September 2026) ────────────────────────
 *
 * The owner put this card beside an exercise row in a plan and asked for one look, naming what the
 * row does: the muscle in white, the pill itself colourless, and the primary/secondary step carried
 * by how strongly that white is lit. The ember the pills took on 16 September — the word in
 * `--accent-ink`, a 16% wash and a 50% ring on a primary — is therefore gone from this object.
 *
 * What is NOT gone is the ember on the FIGURE. The step there is the whole of what the silhouette
 * has to say and the owner's 16.09 round settled it; a plan's exercise row draws no figure at all,
 * so there was nothing to match it to. One card, two objects, and only the one the owner named
 * changed.
 *
 * ── The step, and the two inks it is spent on ────────────────────────────────────────────────
 * Primary takes `--text`, secondary `--text-2` — the app's own two strengths of white, the same
 * pair the plan library's ranked rows use to lift their leader. `--text-3` is NOT available here
 * and the reason is measured and already on the record in this file's history: at this size it
 * reaches 3.01:1 in Flame and 4.02:1 in Flame Dark, which is an AA failure, and it was the ink the
 * level word was rescued from. Measured on `--bg-card`, which is now the ground under EVERY pill
 * because the wash is gone:
 *
 * |                              | Flame (light) | Flame Dark |
 * |---|---|---|
 * | group word, primary, `--text`   | 18.86:1 | 18.07:1 |
 * | group word, secondary, `--text-2` | 6.63:1 | 8.96:1 |
 * | level word, `--text-2`          | 6.63:1 | 8.96:1 |
 *
 * ── The dot follows the WORD, through a local rather than through `currentColor` ─────────────
 * It used to arrive per pill as an inline custom property carrying the model's `ink`, so the dot
 * and the figure could not disagree. With the pill on white that tie is not just unnecessary, it is
 * wrong — an ember dot would be the one coloured thing left on a pill the owner asked to make
 * colourless. So the dot takes the pill's own ink instead.
 *
 * `currentColor` would say that in one word and the value allowlist refuses it in a `background` —
 * a keyword is a value it cannot check a token against. `--pill-dot` is the seam this file already
 * used for the dot, so it keeps it: each of the two rules below sets the pair, and the pair cannot
 * be set apart without the diff showing both lines.
 *
 * Nothing here is carried by colour alone: the pill states the group in words and the level in
 * words, exactly as it did. */
.pill {
  --pill-dot: var(--text-2);

  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  height: 23px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

/* The whole of the primary step: the word, and the dot beside it, lit to full white. No fill and no
 * ring — a primary pill and a secondary one are the same colourless object at two strengths. */
.pillPrimary {
  --pill-dot: var(--text);

  color: var(--text);
}

.pillDot {
  display: block;
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--pill-dot);
  border-radius: var(--radius-hair);
}

.pillLevel {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-day);
  text-transform: uppercase;
  color: var(--text-2);
}
